import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useBooking } from '../../../store/bookingStore';
import { useBookingStudio } from '../../../hooks/useBookingStudio';
import TotalBox from './TotalBox';
import { parseLocalDateString } from '../../../util/parseLocalDate';
import {
  buildBookingCheckoutPayload,
  buildBookingPayloadSignature,
  getBookingDurationInHours,
} from '../../../util/bookingPayload';
import {
  buildBookingAuthNavigationState,
  buildStudioBookingRedirectLocation,
  isBookingAuthError,
  storeStudioBookingRedirect,
} from '../../../util/bookingAuthRedirect';
import { getReservationStripeRoute } from '../../../util/stripeReservationRoute';
import { getReservationAmountGate } from '../../../util/bookingServerAmountSync';
import { logBookingDiagnostic } from '../../../util/bookingDiagnostics';
import { trackBookingPurchased } from '../../../util/tracking';
import PreparedStripePaymentSection from '../PreparedStripePaymentSection';
import { usePreparedBookingPaymentFlow } from '../usePreparedBookingPaymentFlow';
import classes from './Checkout.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const DIAG = '[Booking Checkout Phone]';
const PAYMENT_DETAILS_ERROR_MESSAGE = 'Zahlungsdaten konnten nicht geprüft werden. Bitte prüfe deine Angaben.';
const PAYMENT_CONFIRMATION_ERROR_MESSAGE = 'Zahlung konnte nicht bestätigt werden. Bitte prüfe deine Zahlungsdaten oder versuche es erneut.';
const BOOKING_PREPARATION_ERROR_MESSAGE = 'Die Buchung konnte gerade nicht vorbereitet werden. Bitte versuche es erneut.';
const BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE = 'Deine Zahlung wurde autorisiert, aber die Buchung konnte nicht erstellt werden. Bitte kontaktiere den Support.';
const BOOKING_AUTH_RECOVERY_MESSAGE = 'Deine Zahlung wurde autorisiert. Bitte melde dich erneut an, damit wir die Buchung abschließen können.';
const SERVER_AMOUNT_REFRESH_MESSAGE = 'Der finale Buchungsbetrag wurde aktualisiert. Bitte prüfe den Betrag und bestätige die Zahlung erneut.';

const PAYMENT_FLOW_MESSAGES = {
  bookingPreparationError: BOOKING_PREPARATION_ERROR_MESSAGE,
  paymentDetailsError: PAYMENT_DETAILS_ERROR_MESSAGE,
  paymentConfirmationError: PAYMENT_CONFIRMATION_ERROR_MESSAGE,
};

const createUserFacingError = (message) => {
  const error = new Error(message);
  error.userFacing = true;
  return error;
};

export const Checkout = ({ setCurrentSlide, nextSlide, data, studioCreativesData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingData, updateBookingData } = useBooking();
  const { handleSlotReservation, handleBookingConfirmation, loading, error } = useBookingStudio();
  const [bookingError, setBookingError] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [priceInfo, setPriceInfo] = useState({ basePrice: 0, totalPrice: 0, durationInHours: 0 });
  const reservationCacheRef = useRef(null);

  const isPackagePricing = useMemo(() => data?.pricingMode === 'PACKAGES', [data?.pricingMode]);
  const paymentMethodRef = useRef(null);

  const stripeUser = data?.User;
  const initialStripeRoute = useMemo(() => {
    const accountId = stripeUser?.stripeConnectAccountId || null;
    return {
      canUseConnect: Boolean(
        accountId
          && stripeUser?.stripeChargesEnabled === true
          && stripeUser?.stripePayoutsEnabled === true
          && stripeUser?.stripeOnboardingComplete === true
      ),
      stripeConnectAccountId: accountId,
    };
  }, [
    stripeUser?.stripeConnectAccountId,
    stripeUser?.stripeChargesEnabled,
    stripeUser?.stripePayoutsEnabled,
    stripeUser?.stripeOnboardingComplete,
  ]);
  const [serverStripeRoute, setServerStripeRoute] = useState(null);

  useEffect(() => {
    setServerStripeRoute(null);
    reservationCacheRef.current = null;
  }, [data?.id]);

  const activeStripeRoute = serverStripeRoute || initialStripeRoute;
  const canUseConnect = activeStripeRoute.canUseConnect;
  const stripeConnectAccountId = activeStripeRoute.stripeConnectAccountId;

  const handleReservationStripeRoute = useCallback((reservation) => {
    const route = getReservationStripeRoute(reservation);
    if (!route) return;

    setServerStripeRoute((previousRoute) => {
      if (
        previousRoute?.canUseConnect === route.canUseConnect
        && previousRoute?.stripeConnectAccountId === route.stripeConnectAccountId
      ) {
        return previousRoute;
      }
      return route;
    });
  }, []);

  const totalAmountCents = useMemo(() => {
    const fromStore = Number(bookingData?.totalPrice);
    if (!Number.isFinite(fromStore) || fromStore <= 0) return 0;
    return Math.round(fromStore * 100);
  }, [bookingData?.totalPrice]);

  const pricingUnavailableMessage =
    bookingData?.pricingUnavailableReason || 'Preis für diese Personengruppe nicht verfügbar.';
  const canPreparePayment =
    !bookingData?.pricingUnavailable
    && totalAmountCents > 0
    && bookingData?.date
    && bookingData?.startTime
    && bookingData?.endTime;

  const ensureReservationAmountReady = useCallback((reservation) => {
    const gate = getReservationAmountGate({
      reservation,
      currentAmountCents: totalAmountCents,
    });

    if (!gate.needsUserReconfirmation) {
      return true;
    }

    gate.updateEntries.forEach(([field, value]) => {
      updateBookingData(field, value);
    });
    setBookingError(SERVER_AMOUNT_REFRESH_MESSAGE);
    return false;
  }, [totalAmountCents, updateBookingData]);

  const buildBookingPayload = useCallback(() => {
    return buildBookingCheckoutPayload({
      bookingData,
      studioId: data?.id,
      isPackagePricing,
    });
  }, [bookingData, data?.id, isPackagePricing]);

  const buildPayloadSignature = useCallback(buildBookingPayloadSignature, []);

  const validateProjectFields = useCallback(() => {
    const errors = {};
    if (!bookingData.projectName || bookingData.projectName.trim().length < 3) {
      errors.projectName = 'Projektname muss mindestens 3 Zeichen lang sein';
    }
    if (!bookingData.description || bookingData.description.trim().length < 10) {
      errors.projectDescription = 'Projektbeschreibung muss mindestens 10 Zeichen lang sein';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [bookingData.projectName, bookingData.description]);

  const runSlotReservation = useCallback(
    (payload) => handleSlotReservation(payload, studioCreativesData),
    [handleSlotReservation, studioCreativesData]
  );

  const handleAuthRequired = useCallback((payload) => {
    const target = buildStudioBookingRedirectLocation({
      studioId: payload?.studioID || data?.id,
      isPhoneSize: true,
      currentLocation: location,
    });

    storeStudioBookingRedirect({
      studioId: payload?.studioID || data?.id,
      isPhoneSize: true,
      currentLocation: location,
    });

    navigate('/anmelden', {
      state: buildBookingAuthNavigationState({ location, from: target }),
    });
  }, [data?.id, location, navigate]);

  const finalizeBookingAfterAuthorizedPayment = useCallback(
    async (reservation, payload) => {
      let bookingResult = null;
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
        try {
          bookingResult = await handleBookingConfirmation(reservation, payload, studioCreativesData);
          if (bookingResult?.success && bookingResult?.booking) break;
        } catch (retryError) {
          if (isBookingAuthError(retryError)) {
            logBookingDiagnostic(DIAG, 'booking confirmation requires auth after authorized payment', { error: retryError });
            handleAuthRequired(payload);
            throw createUserFacingError(BOOKING_AUTH_RECOVERY_MESSAGE);
          }
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          } else {
            logBookingDiagnostic(DIAG, 'booking confirmation retry failed', { error: retryError });
            throw createUserFacingError(BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE);
          }
        }
      }
      if (!bookingResult?.success || !bookingResult?.booking) {
        throw createUserFacingError(BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE);
      }
      updateBookingData('paymentDraft', null);
      updateBookingData('id', bookingResult.booking.id);
      trackBookingPurchased({
        booking: bookingResult.booking,
        payload,
        reservation,
        source: "booking_phone",
      });
      nextSlide();
    },
    [handleBookingConfirmation, studioCreativesData, updateBookingData, nextSlide, handleAuthRequired]
  );

  const paymentFlow = usePreparedBookingPaymentFlow({
    autoPrepare: true,
    canPreparePayment,
    validateProjectFields,
    buildBookingPayload,
    buildPayloadSignature,
    runSlotReservation,
    finalizeBookingAfterAuthorizedPayment,
    setBookingError,
    setPaymentProcessing,
    paymentProcessing,
    loading,
    canUseConnect,
    stripeConnectAccountId,
    onReservationStripeRoute: handleReservationStripeRoute,
    ensureReservationAmountReady,
    reservationCacheRef,
    onAuthRequired: handleAuthRequired,
    diagnosticLabel: DIAG,
    source: 'booking_phone',
    messages: PAYMENT_FLOW_MESSAGES,
  });

  useEffect(() => {
    if (!bookingData?.startTime || !bookingData?.endTime || !bookingData?.date || (!data?.pricePerHour && !isPackagePricing)) return;
    const durationInHours = getBookingDurationInHours(bookingData.startTime, bookingData.endTime);
    if (durationInHours <= 0) return;

    let basePrice = 0;
    const totalPrice = Number(bookingData?.totalPrice) || 0;

    if (isPackagePricing) {
      basePrice = Number(bookingData?.totalStudioPrice) || 0;
    } else {
      basePrice = Number(bookingData?.baseStudioPrice) || durationInHours * data.pricePerHour;
    }

    setPriceInfo({ basePrice, totalPrice, durationInHours });
  }, [
    bookingData?.startTime,
    bookingData?.endTime,
    bookingData?.date,
    bookingData?.baseStudioPrice,
    bookingData?.totalStudioPrice,
    bookingData?.totalPrice,
    data?.pricePerHour,
    isPackagePricing,
  ]);

  const preparedReservation = paymentFlow.preparedPayment?.reservation || null;

  const elementsOptions = useMemo(
    () => ({
      clientSecret: preparedReservation?.clientSecret,
      loader: 'auto',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#1F1F1F',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          borderRadius: '8px',
        },
      },
    }),
    [preparedReservation?.clientSecret]
  );

  const elementsInstanceKey = useMemo(
    () => `prepared-${data?.id || 'na'}-${preparedReservation?.paymentIntentID || preparedReservation?.slotID || 'none'}`,
    [data?.id, preparedReservation?.paymentIntentID, preparedReservation?.slotID]
  );

  const formatDate = (date) => {
    const parsed = parseLocalDateString(date);
    if (!parsed) return '';
    return parsed.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };
  const { basePrice, totalPrice, durationInHours } = priceInfo;

  return (
    <div className={classes.container}>
      {data && bookingData && (
        <TotalBox
          setCurrentSlide={setCurrentSlide}
          pricePerHour={data?.pricePerHour}
          basePrice={basePrice}
          totalPrice={totalPrice}
          durationInHours={durationInHours}
          date={formatDate(bookingData?.date)}
          startTime={bookingData?.startTime}
          endTime={bookingData?.endTime}
          personCount={bookingData?.numberOfPeople}
          data={data}
          studioCreativesData={studioCreativesData}
        />
      )}
      <div className={classes.paymentContainer}>
        <h2 className={`${classes.paymentTitle} titleSemibold3`}>Bezahlen mit</h2>
        <div className={classes.paymentOptions} ref={paymentMethodRef}>
          {!canPreparePayment ? (
            <p className="slimRegularSmallText">
              {bookingData?.pricingUnavailable
                ? pricingUnavailableMessage
                : 'Bitte zuerst Datum und Zeit wählen.'}
            </p>
          ) : paymentFlow.status === 'preparing' || (
            paymentFlow.status === 'idle'
            && !preparedReservation?.clientSecret
            && !paymentFlow.autoPrepareAttempted
          ) ? (
            <div className={classes.paymentPreparationSkeleton} aria-live="polite">
              <div className={`${classes.stripeLoadingMessage} slimRegularSmallText`}>
                Buchung wird geprüft...
              </div>
              <div className={classes.paymentSkeletonLine} />
              <div className={classes.paymentSkeletonLine} />
            </div>
          ) : preparedReservation?.clientSecret ? (
            <Elements key={elementsInstanceKey} stripe={stripePromise} options={elementsOptions}>
              <PreparedStripePaymentSection
                classes={classes}
                buttonClassName={`${classes.bookingConfirmationButton} regularLarge`}
                paymentFlow={paymentFlow}
                paymentProcessing={paymentProcessing}
                loading={loading}
              />
            </Elements>
          ) : (
            <button
              type="button"
              onClick={() => paymentFlow.prepareBookingPayment()}
              className={`${classes.bookingConfirmationButton} regularLarge`}
              disabled={paymentProcessing || loading}
            >
              {paymentFlow.status === 'expired' || paymentFlow.status === 'error'
                ? 'Buchung erneut prüfen'
                : 'Weiter zur Zahlung'}
            </button>
          )}
          {fieldErrors?.paymentMethod && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.paymentMethod}</span></div>}
          {fieldErrors?.projectName && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.projectName}</span></div>}
          {fieldErrors?.projectDescription && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.projectDescription}</span></div>}
        </div>
      </div>
      <div className={classes.horizontalLine} />
      <div className={classes.bookingConfirmation}>
        <p className="slimRegularSmallText">Mit der Bestätigung der Zahlung akzeptiere ich Stus Nutzungsbedingungen, Gemeinschaftsrichtlinien und Stornierungsbedingungen.</p>
        {bookingError && <div className={classes.errorMessage}><span className="slimRegularSmallText">{bookingError}</span></div>}
        {error && <div className={classes.errorMessage}><span className="slimRegularSmallText">Die Buchung konnte nicht erstellt werden. Bitte versuche es erneut.</span></div>}
      </div>
    </div>
  );
};

export default Checkout;
